import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { convertToUTC, convertFromUTC, formatTimeWithZone } from "./timezoneUtils";
import { resolveRequiredMinGroundTime, buildMissingMinGroundTimeError } from "./minGroundTimeUtils";

// 加载dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

// ========== 文档说明 ==========

/**
 * 时区处理最佳实践
 *
 * 我们系统中的时区处理遵循以下原则：
 *
 * 1. 数据库和API层：
 *    - 始终存储UTC格式的时间 (ISO 8601 格式，以Z结尾)
 *    - API响应返回UTC时间
 *    - API请求接收UTC时间
 *
 * 2. 前端处理：
 *    - 从API获取数据后，使用convertFromUTC转换为用户选择的时区进行显示
 *    - 用户输入的时间被视为用户在页面上选择的时区的时间 (通过TimeZoneSelector组件选择，组件在系统中所有页面上方)
 *    - 表单提交前，使用convertToUTC将用户选择的时区的时间转换为UTC时间
 *
 * 3. 航班时间计算：
 *    - 所有内部时间计算都使用UTC时间进行
 *    - 跨天判断基于用户选择的时区
 *    - 航班日期、起飞时间(STD)和到达时间(STA)的关系一致性由ensureFlightTimeConsistency函数维护
 *
 * 4. 核心时区转换函数：
 *    - convertToUTC: 将本地时区时间转换为UTC
 *    - convertFromUTC: 将UTC时间转换为本地时区
 *    - formatTimeWithZone: 将UTC时间格式化为指定时区的显示字符串
 *    - isSameDay: 判断两个时间在指定时区是否是同一天
 *
 * 注意事项：
 * - 在使用时区相关函数时，始终明确传递timeZone参数
 * - 理解"date"和"datetime"的区别：date只有日期部分，datetime包含日期和时间
 * - 航班跨天情况需特别注意，确保使用ensureFlightTimeConsistency处理
 */

// ========== 基础时间处理 ==========

/**
 * 标准日期显示格式
 */
export const DATE_FORMATS = {
    DATE: "YYYY-MM-DD",
    TIME: "HH:mm",
    DATE_TIME: "YYYY-MM-DD HH:mm",
    DATE_TIME_SECONDS: "YYYY-MM-DD HH:mm:ss",
    DATE_TIME_TZ: "YYYY-MM-DDTHH:mm:ssZ",
    MONTH_DAY: "MM-DD",
    YEAR_MONTH: "YYYY-MM",
    DISPLAY_DATE: "YYYY年MM月DD日",
    DISPLAY_DATE_TIME: "YYYY年MM月DD日 HH:mm",
    SHORT_DATE: "MM/DD",
    DAY_MONTH: "D MMM", // 5 Jun
};

/**
 * 检查日期对象是否有效
 * @param {string|Date|dayjs.Dayjs} date - 日期对象或字符串
 * @returns {boolean} 是否为有效日期
 */
export const isValidDate = (date) => {
    if (!date) return false;
    return dayjs(date).isValid();
};

/**
 * 验证并格式化日期
 * @param {string|Object} dateValue - 日期字符串或对象
 * @param {string} format - 输出格式 (默认 YYYY-MM-DD)
 * @returns {string} 格式化的日期字符串
 */
export const formatDate = (dateValue, format = "YYYY-MM-DD") => {
    try {
        const dateObj = dayjs(dateValue);
        if (!dateObj.isValid()) {
            console.warn("无效的日期:", dateValue);
            return dayjs().format(format); // 默认当天
        }
        return dateObj.format(format);
    } catch (error) {
        console.error("日期解析错误:", error, dateValue);
        return dayjs().format(format);
    }
};

/**
 * 创建标准ISO时间字符串
 * @param {dayjs.Dayjs|string|Date} dateStr - 日期部分
 * @param {dayjs.Dayjs|string|object} timeStr - 时间部分
 * @param {string} [fallback=null] - 当转换失败时返回的默认值
 * @returns {string} ISO格式的时间字符串
 */
export const createIsoTimeString = (dateStr, timeStr, fallback = null) => {
    try {
        // 记录调试信息
        const debugInfo = {
            dateType: typeof dateStr,
            timeType: typeof timeStr,
            dateIsObject: dateStr && typeof dateStr === "object",
            timeIsObject: timeStr && typeof timeStr === "object",
            dateProps: dateStr && typeof dateStr === "object" ? Object.keys(dateStr).join(",") : null,
            timeProps: timeStr && typeof timeStr === "object" ? Object.keys(timeStr).join(",") : null,
            dateOriginalValue: dateStr ? (JSON.stringify(dateStr) || "序列化失败").substring(0, 100) : "null",
            timeOriginalValue: timeStr ? (JSON.stringify(timeStr) || "序列化失败").substring(0, 100) : "null",
        };

        // 处理null或undefined值
        if (!dateStr && !timeStr) {
            console.warn("日期和时间都为空", debugInfo);
            return fallback;
        }

        // 特殊情况处理：如果日期是null但时间不是，使用当天日期
        if (!dateStr && timeStr) {
            console.warn("日期为空但时间有值，使用当天日期", debugInfo);
            dateStr = dayjs().startOf("day");
        }

        // 特殊情况处理：如果时间是null但日期不是，使用午夜时间
        if (dateStr && !timeStr) {
            console.warn("时间为空但日期有值，使用午夜时间", debugInfo);
            timeStr = "00:00:00";
        }

        // 转换时间为dayjs对象 - 增强处理dayjs对象
        let dayjsTime = null;
        if (timeStr) {
            if (timeStr.isValid && typeof timeStr.isValid === "function") {
                // 已经是dayjs对象
                dayjsTime = timeStr;
            } else if (timeStr.$d && timeStr.$d instanceof Date) {
                // 类dayjs对象，有$d属性
                dayjsTime = dayjs(timeStr.$d);
            } else if (typeof timeStr === "string") {
                // 字符串格式，如 "08:30" 或 "08:30:00"
                if (timeStr.includes("T")) {
                    // ISO格式 "2023-04-01T08:30:00"
                    dayjsTime = dayjs(timeStr);
                } else {
                    // 普通时间格式 "08:30" 或 "08:30:00"
                    dayjsTime = dayjs(`2000-01-01T${timeStr}`);
                }
            } else if (timeStr instanceof Date) {
                // 原生Date对象
                dayjsTime = dayjs(timeStr);
            }

            // 验证转换结果
            if (!dayjsTime || !dayjsTime.isValid()) {
                console.warn("时间转换失败，使用午夜时间", debugInfo, timeStr);
                dayjsTime = dayjs().startOf("day"); // 使用当天午夜
            }
        } else {
            // 默认午夜时间
            dayjsTime = dayjs().startOf("day");
        }

        // 尝试创建日期时间
        const result = createDateTime(dateStr, dayjsTime, {
            format: "YYYY-MM-DDTHH:mm:ss",
            fallback: null,
        });

        if (!result) {
            console.warn("创建日期时间失败", debugInfo);
            return fallback;
        }

        return `${result}Z`;
    } catch (error) {
        console.error("创建ISO时间字符串错误:", error, "日期:", typeof dateStr, dateStr, "时间:", typeof timeStr, timeStr);
        return fallback;
    }
};

/**
 * 从日期时间字符串中提取时间部分
 * @param {string|Object} dateTimeStr - 日期时间字符串或dayjs对象
 * @returns {string|null} 时间部分 (HH:mm)
 */
export const extractTimeFromDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return null;

    try {
        // 首先检查是否是dayjs对象
        if (dateTimeStr.isValid && typeof dateTimeStr.isValid === "function" && dateTimeStr.isValid()) {
            // 对于dayjs对象，直接使用其format方法
            // 检查是否是utc模式，保持一致性
            if (dateTimeStr.$u === true) {
                return dateTimeStr.utc().format("HH:mm");
            } else {
                return dateTimeStr.format("HH:mm");
            }
        }

        // 以下处理字符串情况
        // 明确使用UTC时间解析，避免本地时区影响
        // 以Z结尾的ISO字符串已经是UTC时间
        if (typeof dateTimeStr === "string" && dateTimeStr.endsWith("Z")) {
            const date = dayjs.utc(dateTimeStr);
            if (date.isValid()) {
                // 使用UTC时间获取小时和分钟
                const hours = date.utc().hour().toString().padStart(2, "0");
                const minutes = date.utc().minute().toString().padStart(2, "0");
                return `${hours}:${minutes}`;
            }
        } else if (typeof dateTimeStr === "string") {
            // 尝试使用dayjs解析
            const date = dayjs(dateTimeStr);
            if (date.isValid()) {
                return date.format("HH:mm");
            }

            // 回退到手动解析
            if (dateTimeStr.includes("T")) {
                const timePart = dateTimeStr.split("T")[1];
                // 移除末尾的Z和毫秒
                const cleanTimePart = timePart.replace("Z", "").split(".")[0];
                const timeComponents = cleanTimePart.split(":");
                if (timeComponents.length >= 2) {
                    return `${timeComponents[0]}:${timeComponents[1]}`;
                }
            } else if (dateTimeStr.includes(" ")) {
                const parts = dateTimeStr.split(" ");
                if (parts.length > 1) {
                    const timeParts = parts[1].split(":");
                    if (timeParts.length >= 2) {
                        return `${timeParts[0]}:${timeParts[1]}`;
                    }
                }
            }
        }
    } catch (error) {
        console.error("提取时间时出错:", error, dateTimeStr);
    }

    return null;
};

/**
 * 将时间字符串转换为分钟数
 * @param {string} timeStr - 时间字符串 (HH:mm)
 * @returns {number} 分钟数
 */
export const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;

    try {
        // 如果timeStr已经是数字，直接返回
        if (typeof timeStr === "number") {
            return timeStr;
        }

        // 如果是dayjs对象
        if (timeStr.isValid && typeof timeStr.isValid === "function") {
            return timeStr.hour() * 60 + timeStr.minute();
        }

        // 如果是Date对象
        if (timeStr instanceof Date) {
            return timeStr.getHours() * 60 + timeStr.getMinutes();
        }

        const parts = timeStr.split(":");
        if (parts.length < 2) {
            console.warn("时间格式不正确:", timeStr);
            return 0;
        }

        let hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);

        if (isNaN(hours) || isNaN(minutes)) {
            console.warn("时间转换为数字失败:", timeStr);
            return 0;
        }

        // 处理特殊情况 "24:00"
        if (hours === 24) {
            hours = 0;
        }

        return hours * 60 + minutes;
    } catch (error) {
        console.error("时间转换为分钟时出错:", error, timeStr);
        return 0;
    }
};

// ========== 航班时间处理 ==========

/**
 * 计算下一航班时间
 * @param {Object} selectedLeg - 选中的航段
 * @param {Object} prevFlight - 前序航班 (可选)
 * @param {Object} schedule - 计划信息 (可选)
 * @param {Object} transportation_demands - 运输需求 (可选)
 * @param {string} timeZone - 时区 (可选)
 * @param {number} minBlockTimeMinutes - 最小轮档时间 (可选，优先使用)
 * @returns {Object} { date, std, sta } - 航班日期和时间
 */
export const calculateNextFlightTime = (selectedLeg, prevFlight = null, schedule = null, transportation_demands = null, timeZone = "UTC", minBlockTimeMinutes = null, options = {}) => {
    // 无前序航班情况 - 使用计划开始日期
    if (!prevFlight) {
        try {
            // 1. 获取并验证日期
            let date = schedule?.start_date ? formatDate(schedule.start_date) : dayjs.utc().format("YYYY-MM-DD");

            // 2. 默认起飞时间
            let stdTime = "08:00:00";

            // 检查起飞机场是否在运输需求中，如果是，使用运输需求中的首选起飞时间
            if (transportation_demands && Array.isArray(transportation_demands) && transportation_demands.length > 0 && selectedLeg.departure_airport) {
                // 查找匹配的运输需求
                const matchedDemand = transportation_demands.find((demand) => demand.origin === selectedLeg.departure_airport);

                // 如果找到匹配的需求且有首选起飞时间，则使用它
                if (matchedDemand && matchedDemand.preferred_time) {
                    try {
                        let timeStr = "";

                        if (typeof matchedDemand.preferred_time === "string") {
                            // 检查是否为ISO格式的日期时间字符串 (包含T和可能的Z)
                            if (matchedDemand.preferred_time.includes("T")) {
                                const d = dayjs.utc(matchedDemand.preferred_time);
                                if (d.isValid()) {
                                    // 只提取时间部分
                                    timeStr = d.format("HH:mm:ss");
                                } else {
                                    timeStr = "08:00:00";
                                }
                            } else {
                                // 简单的时间字符串 "HH:mm" 或 "HH:mm:ss"
                                timeStr = matchedDemand.preferred_time;
                                if (!timeStr.includes(":")) {
                                    timeStr = "08:00:00";
                                }
                            }
                        } else if (matchedDemand.preferred_time && matchedDemand.preferred_time.$d instanceof Date) {
                            // dayjs对象 - 转换为UTC
                            const d = dayjs.utc(matchedDemand.preferred_time.$d);
                            timeStr = d.format("HH:mm:ss");
                        } else if (matchedDemand.preferred_time instanceof Date) {
                            // Date对象 - 转换为UTC
                            const d = dayjs.utc(matchedDemand.preferred_time);
                            timeStr = d.format("HH:mm:ss");
                        } else if (matchedDemand.preferred_time && typeof matchedDemand.preferred_time === "object") {
                            // 其他对象处理
                            if (typeof matchedDemand.preferred_time.format === "function") {
                                timeStr = matchedDemand.preferred_time.format("HH:mm:ss");
                            } else if (matchedDemand.preferred_time.hour !== undefined && matchedDemand.preferred_time.minute !== undefined) {
                                const hour = matchedDemand.preferred_time.hour.toString().padStart(2, "0");
                                const minute = matchedDemand.preferred_time.minute.toString().padStart(2, "0");
                                timeStr = `${hour}:${minute}:00`;
                            }
                        }

                        // 确保时间格式正确
                        if (timeStr && timeStr.includes(":")) {
                            if (timeStr.split(":").length === 2) {
                                timeStr += ":00"; // 添加秒
                            }
                            stdTime = timeStr;
                        }
                    } catch (timeError) {
                        console.error("解析首选时间出错:", timeError);
                        // 保持默认时间
                    }
                }
            }

            // 3. 创建临时航班对象 (确保UTC格式)
            const tempFlight = {
                flight_date: date,
                std: `${date}T${stdTime}Z`, // 确保UTC时间格式
                min_block_time_minutes: minBlockTimeMinutes !== null ? minBlockTimeMinutes : parseInt(selectedLeg.block_time_minutes) || 120,
            };

            // 4. 计算初始STA (UTC时间)
            const stdDate = dayjs.utc(tempFlight.std);
            // 确保flight_date与std保持完全一致
            tempFlight.flight_date = tempFlight.std;
            tempFlight.sta = stdDate.add(tempFlight.min_block_time_minutes, "minute").format("YYYY-MM-DDTHH:mm:ss") + "Z";

            // 5. 应用一致性处理，确保所有时间关系正确
            const processedFlight = ensureFlightTimeConsistency(tempFlight, timeZone);

            // 返回处理后的时间数据
            return {
                date: processedFlight.flight_date,
                std: processedFlight.std,
                sta: processedFlight.sta,
            };
        } catch (error) {
            console.error("初始航班时间计算错误:", error);

            // 生成可靠的默认值 (UTC格式)
            const defaultDate = dayjs.utc().format("YYYY-MM-DD");
            const defaultStd = dayjs.utc().hour(8).minute(0).second(0).format("YYYY-MM-DDTHH:mm:ss") + "Z";
            const defaultSta = dayjs.utc().hour(10).minute(0).second(0).format("YYYY-MM-DDTHH:mm:ss") + "Z";

            return { date: defaultDate, std: defaultStd, sta: defaultSta };
        }
    }
    // 有前序航班情况 - 使用前序航班到达时间 + 地面时间
    else {
        // 1. 先取最小过站：只认到达站在机场主数据里的明确配置（含 0 分钟）。
        //    缺配置直接抛出，让调用方拦住并提示到达站；不得在下面的兜底 catch 里
        //    变成“到达后再停 1 小时”或其它默认时间。
        const requiredMinGroundTime = resolveRequiredMinGroundTime(prevFlight, options.airportConstraints);
        if (requiredMinGroundTime === null) {
            throw buildMissingMinGroundTimeError(prevFlight);
        }

        try {
            // 1. 获取前序航班的到达时间 (确保UTC格式)
            if (!prevFlight.sta) {
                throw new Error("前序航班缺少到达时间");
            }

            const prevStaDate = dayjs.utc(prevFlight.sta);
            if (!prevStaDate.isValid()) {
                throw new Error("前序航班到达时间无效");
            }

            // 2. 地面时间（分钟）来自机场主数据当前配置；航班上存着的
            //    min_ground_time_minutes 不参与取值
            const gtMinutes = requiredMinGroundTime;

            // 3. 创建临时航班对象 (使用UTC时间)
            const tempFlight = {
                // 初始使用前序航班的日期
                flight_date: prevStaDate.format("YYYY-MM-DD"),
                // 计算STD（前序到达 + 地面时间）
                std: prevStaDate.add(gtMinutes, "minute").format("YYYY-MM-DDTHH:mm:ss") + "Z",
                // 飞行时间
                min_block_time_minutes: minBlockTimeMinutes !== null ? minBlockTimeMinutes : parseInt(selectedLeg.block_time_minutes) || 120,
            };

            // 4. 计算STA（STD + 飞行时间）
            const stdDate = dayjs.utc(tempFlight.std);
            // 确保flight_date与std保持完全一致
            tempFlight.flight_date = tempFlight.std;
            tempFlight.sta = stdDate.add(tempFlight.min_block_time_minutes, "minute").format("YYYY-MM-DDTHH:mm:ss") + "Z";

            // 5. 处理时间一致性，确保flight_date正确反映STD的日期，并处理跨天
            const processedFlight = ensureFlightTimeConsistency(tempFlight, timeZone);

            // 返回处理后的时间数据 (UTC格式)
            return {
                date: processedFlight.flight_date,
                std: processedFlight.std,
                sta: processedFlight.sta,
            };
        } catch (error) {
            console.error("航班时间计算错误:", error);

            // 生成可靠的默认值 (UTC格式)
            const flightDate = prevFlight.flight_date ? dayjs.utc(prevFlight.flight_date).format("YYYY-MM-DD") : dayjs.utc().format("YYYY-MM-DD");

            const defaultStd = dayjs.utc(flightDate + "T08:00:00Z").format("YYYY-MM-DDTHH:mm:ss") + "Z";
            const defaultSta = dayjs.utc(flightDate + "T10:00:00Z").format("YYYY-MM-DDTHH:mm:ss") + "Z";

            return { date: flightDate, std: defaultStd, sta: defaultSta };
        }
    }
};

/**
 * 生成航班号
 * @param {string} prefix - 航班号前缀，通常是航司两字代码
 * @returns {string} 航班号
 */
export const generateFlightNumber = (prefix = "XX") => {
    // 确保前缀有效
    const validPrefix = prefix?.trim() || "XX";

    // 生成三位数字序号
    const sequence = Math.floor(Math.random() * 900) + 100;

    return `${validPrefix}${sequence}`;
};

/**
 * 检查时间是否满足最小间隔要求
 * @param {string} time1 - 第一个时间 (ISO格式或HH:mm)
 * @param {string} time2 - 第二个时间 (ISO格式或HH:mm)
 * @param {number} minMinutes - 最小间隔分钟数
 * @returns {boolean} 是否满足最小间隔
 */
export const hasMinimumTimeDifference = (time1, time2, minMinutes) => {
    if (!time1 || !time2 || !minMinutes) return true;

    try {
        // 尝试直接使用dayjs计算差异（针对ISO格式）
        const date1 = dayjs(time1);
        const date2 = dayjs(time2);

        if (date1.isValid() && date2.isValid()) {
            return date2.diff(date1, "minute") >= minMinutes;
        }

        // 回退到分钟计算
        const t1 = timeToMinutes(extractTimeFromDateTime(time1) || time1);
        const t2 = timeToMinutes(extractTimeFromDateTime(time2) || time2);

        let diff = t2 - t1;

        // 处理跨天的情况
        if (diff < 0) {
            diff += 24 * 60; // 加上一天的分钟数
        }

        return diff >= minMinutes;
    } catch (error) {
        console.error("检查最小时间间隔时出错:", error, { time1, time2, minMinutes });
        return false;
    }
};

/**
 * 格式化规则时间范围为易读形式
 * @param {Object} rule - 时间规则对象 { start: "HH:mm", end: "HH:mm" } 或 { start_time: "HH:mm", end_time: "HH:mm" }
 * @returns {string} 格式化的时间范围
 */
export const formatTimeRangeRule = (rule) => {
    if (!rule) return "未定义";

    // 支持两种字段命名
    const startTime = rule.start_time || rule.start;
    const endTime = rule.end_time || rule.end;

    if (!startTime || !endTime) return "未定义";

    // 将formatPart提升到函数作用域
    const formatPart = (timeStr) => {
        return timeStr?.split(":").slice(0, 2).join(":") || "--:--";
    };

    try {
        const start = formatPart(startTime);
        const end = formatPart(endTime);

        // 原有逻辑保持不变，使用处理后的时间
        const startMinutes = timeToMinutes(start);
        const endMinutes = timeToMinutes(end);

        if (startMinutes > endMinutes) {
            return `${start}-${end} (+1)`; // 跨天标记
        }

        return `${start}-${end}`;
    } catch (error) {
        console.error("格式化时间范围时出错:", error, rule);
        // 现在可以正确访问formatPart
        return `${formatPart(startTime)}-${formatPart(endTime)}`;
    }
};

// ========== 类型处理与安全操作 ==========

/**
 * 检查并确保返回有效的dayjs对象
 * @param {any} value - 需要检查的值
 * @param {dayjs.Dayjs} [fallback=null] - 当值无效时的默认返回值
 * @return {dayjs.Dayjs|null} 有效的dayjs对象或fallback值
 */
export const ensureDayjsObject = (value, fallback = null) => {
    try {
        // 处理null或undefined
        if (value == null) return fallback;

        // 检查是否已经是dayjs对象的不同情况
        // 1. 是标准的dayjs对象，有isValid方法
        if (value.isValid && typeof value.isValid === "function") {
            return value.isValid() ? value : fallback;
        }

        // 2. 有dayjs特征但可能缺少isValid方法 ($d, $L, $y等是dayjs内部属性)
        if (value.$d instanceof Date || value._isAMomentObject || value.$L) {
            // 创建一个新的dayjs对象以确保它有所有需要的方法
            const newDayjs = dayjs(value.$d || value);
            return newDayjs.isValid() ? newDayjs : fallback;
        }

        // 3. 是普通Date对象
        if (value instanceof Date) {
            const dayjsFromDate = dayjs(value);
            return dayjsFromDate.isValid() ? dayjsFromDate : fallback;
        }

        // 尝试转换为dayjs对象 (字符串、时间戳等)
        const dayjsObj = dayjs(value);
        if (!dayjsObj.isValid()) {
            console.warn("无法转换为有效的dayjs对象:", value);
            return fallback;
        }
        return dayjsObj;
    } catch (error) {
        console.error("dayjs对象转换错误:", error, typeof value, value);
        return fallback;
    }
};

/**
 * 安全获取dayjs对象的属性或调用方法
 * @param {any} value - dayjs对象或可转换为dayjs的值
 * @param {string} methodName - 要调用的方法名
 * @param {Array} [args=[]] - 传递给方法的参数
 * @param {*} [defaultValue=null] - 操作失败时的默认返回值
 * @return {*} 方法调用结果或默认值
 */
export const safelyCallDayjsMethod = (value, methodName, args = [], defaultValue = null) => {
    try {
        const dayjsObj = ensureDayjsObject(value);
        if (!dayjsObj) return defaultValue;

        // 如果是属性而非方法
        if (typeof dayjsObj[methodName] !== "function") {
            return dayjsObj[methodName] ?? defaultValue;
        }

        // 调用方法
        const result = dayjsObj[methodName](...args);
        return result !== undefined ? result : defaultValue;
    } catch (error) {
        console.error(`dayjs方法'${methodName}'调用错误:`, error, value);
        return defaultValue;
    }
};

/**
 * 安全添加时间单位到日期对象
 * @param {any} value - dayjs对象或可转换为dayjs的值
 * @param {number} amount - 要添加的数量
 * @param {string} unit - 时间单位 (day, month, minute等)
 * @param {dayjs.Dayjs} [fallback=null] - 当值无效时的默认返回值
 * @return {dayjs.Dayjs|null} 新的dayjs对象或null
 */
export const safelyAddTime = (value, amount, unit, fallback = null) => {
    try {
        // 内部自动处理dayjs对象转换
        const dayjsObj = ensureDayjsObject(value, fallback);
        if (!dayjsObj) return fallback;

        return dayjsObj.add(amount, unit);
    } catch (error) {
        console.error(`添加时间失败:`, error, { value, amount, unit });
        return fallback;
    }
};

/**
 * 安全格式化日期
 * @param {any} value - dayjs对象或可转换为dayjs的值
 * @param {string} format - 输出格式
 * @param {string} [defaultValue=""] - 格式化失败时的默认值
 * @return {string} 格式化后的字符串或默认值
 */
export const safelyFormatDate = (value, format, defaultValue = "") => {
    try {
        // 内部自动处理dayjs对象转换
        const dayjsObj = ensureDayjsObject(value);
        if (!dayjsObj) return defaultValue;

        return dayjsObj.format(format);
    } catch (error) {
        console.error(`日期格式化失败:`, error, { value, format });
        return defaultValue;
    }
};

/**
 * 创建或合并ISO格式的日期和时间
 * @param {any} dateValue - 日期部分 (可以是日期对象或字符串)
 * @param {any} timeValue - 时间部分 (可以是时间对象或字符串)
 * @param {Object} options - 选项
 * @param {boolean} [options.returnDayjs=false] - 是否返回dayjs对象而非字符串
 * @param {string} [options.format="YYYY-MM-DDTHH:mm:ss"] - 输出格式
 * @param {dayjs.Dayjs|string} [options.fallback=""] - 失败时的返回值
 * @return {string|dayjs.Dayjs|null} 合并后的日期时间
 */
export const createDateTime = (dateValue, timeValue, { returnDayjs = false, format = "YYYY-MM-DDTHH:mm:ss", fallback = "" } = {}) => {
    try {
        // 记录诊断信息
        const debugInfo = {
            dateValueType: typeof dateValue,
            timeValueType: typeof timeValue,
            dateHasProperties: dateValue && typeof dateValue === "object" ? Object.keys(dateValue).slice(0, 5).join(",") : "N/A",
            timeHasProperties: timeValue && typeof timeValue === "object" ? Object.keys(timeValue).slice(0, 5).join(",") : "N/A",
        };

        // 确保有效的日期 - 内部自动处理转换
        const dateObj = ensureDayjsObject(dateValue);
        if (!dateObj) {
            console.warn("无效的日期值:", debugInfo, dateValue);
            return returnDayjs ? null : fallback;
        }

        let result = dateObj;

        if (timeValue) {
            try {
                // 获取时间组件
                let hours = 0,
                    minutes = 0,
                    seconds = 0;

                // 如果timeValue是dayjs对象或可转换为dayjs对象
                const timeObj = ensureDayjsObject(timeValue);
                if (timeObj) {
                    hours = timeObj.hour();
                    minutes = timeObj.minute();
                    seconds = timeObj.second();
                }
                // 如果是特殊的时间对象（有小时和分钟属性）
                else if (timeValue && typeof timeValue === "object" && "hour" in timeValue && "minute" in timeValue) {
                    hours = parseInt(timeValue.hour, 10) || 0;
                    minutes = parseInt(timeValue.minute, 10) || 0;
                    seconds = parseInt(timeValue.second, 10) || 0;
                }
                // 如果是特殊的Moment-like对象
                else if (timeValue && typeof timeValue === "object" && timeValue.$H !== undefined && timeValue.$m !== undefined) {
                    hours = parseInt(timeValue.$H, 10) || 0;
                    minutes = parseInt(timeValue.$m, 10) || 0;
                    seconds = parseInt(timeValue.$s, 10) || 0;
                }
                // 如果是字符串 "HH:mm:ss" 或 "HH:mm"
                else if (typeof timeValue === "string") {
                    const timeParts = timeValue.split(":");
                    if (timeParts.length >= 2) {
                        hours = parseInt(timeParts[0], 10) || 0;
                        minutes = parseInt(timeParts[1], 10) || 0;
                        seconds = timeParts.length > 2 ? parseInt(timeParts[2], 10) || 0 : 0;
                    }
                }
                // 处理极端情况 - 尝试从Date对象提取时间
                else if (timeValue instanceof Date || (timeValue && timeValue.$d instanceof Date)) {
                    const date = timeValue instanceof Date ? timeValue : timeValue.$d;
                    hours = date.getHours();
                    minutes = date.getMinutes();
                    seconds = date.getSeconds();
                }

                // 防止无效的小时/分钟值
                hours = Math.max(0, Math.min(23, hours));
                minutes = Math.max(0, Math.min(59, minutes));
                seconds = Math.max(0, Math.min(59, seconds));

                // 合并日期和时间
                result = dateObj.hour(hours).minute(minutes).second(seconds);

                // 最终验证
                if (!result.isValid()) {
                    throw new Error("合并后的日期时间无效");
                }
            } catch (innerError) {
                console.error("处理时间部分时出错:", innerError, { timeValue, debugInfo });
                // 失败时回退到只使用日期部分
                result = dateObj;
            }
        }

        return returnDayjs ? result : result.format(format);
    } catch (error) {
        console.error("创建日期时间错误:", error, { dateValue, timeValue });
        return returnDayjs ? null : fallback;
    }
};

/**
 * 计算距离宵禁时间的分钟数
 * @param {string} timeStr - 时间字符串，格式为HH:MM
 * @param {Object} curfewRule - 宵禁规则对象
 * @returns {number|null} 返回距离宵禁开始的分钟数，如果不在宵禁时间前，则返回null
 */
export const getMinutesToCurfew = (timeStr, curfewRule) => {
    if (!timeStr || !curfewRule) return null;

    try {
        // 支持两种字段命名
        const startTime = curfewRule.start_time || curfewRule.start;
        const endTime = curfewRule.end_time || curfewRule.end;

        if (!startTime) return null;

        // 解析当前时间和宵禁开始时间
        const [hour, minute] = timeStr.split(":").map(Number);
        const [curfewHour, curfewMinute] = startTime.split(":").map(Number);

        // 转换为分钟计算
        const currentMinutes = hour * 60 + minute;
        const curfewMinutes = curfewHour * 60 + curfewMinute;

        // 判断是否在宵禁开始前
        if (currentMinutes < curfewMinutes) {
            // 如果当前时间小于宵禁开始时间，返回相差的分钟数
            return curfewMinutes - currentMinutes;
        } else if (endTime) {
            // 检查是否是跨天宵禁
            const [curfewEndHour, curfewEndMinute] = endTime.split(":").map(Number);
            const curfewEndMinutes = curfewEndHour * 60 + curfewEndMinute;

            // 如果宵禁时间是跨天的（例如从23:00到05:00）
            if (curfewEndMinutes < curfewMinutes) {
                // 如果当前时间大于宵禁结束时间并且小于宵禁开始时间
                if (currentMinutes > curfewEndMinutes && currentMinutes < curfewMinutes) {
                    return curfewMinutes - currentMinutes;
                }
            }
        }

        return null; // 不在宵禁时间前
    } catch (error) {
        console.error("计算距离宵禁时间出错:", error);
        return null;
    }
};

/**
 * 宵禁结束后的缓冲分钟数：
 * - 重算的处理范围是 [宵禁开始, 宵禁结束 + 缓冲)，落点为"宵禁结束 + 缓冲"的精确值；
 * - 校验提示在时间落在 [宵禁结束, 宵禁结束 + 缓冲) 内时给"距宵禁结束不足 N 分钟"提示。
 */
export const CURFEW_END_BUFFER_MINUTES = 5;

/**
 * 计算时间距宵禁结束的分钟数（"距结束"方向）。
 *
 * 只做时刻运算：时间落在 [宵禁结束, 宵禁结束 + bufferMinutes) 内时返回间隔分钟数
 * （含 0——正好落在结束整点也算），其余返回 null。跨天结束时刻的分钟差按回绕处理
 * （例如宵禁 20:00-00:00，00:02 距结束 2 分钟）。
 * 规则适用性（星期、有效期、豁免）由调用方判断：S3 提示方向按"宵禁开始那天"锚定，
 * 重算缓冲带方向跟随本端违规判定口径（按检查时刻所在天），两个调用方口径不同。
 *
 * @param {string} timeStr - 时间字符串，格式为HH:MM
 * @param {Object} curfewRule - 宵禁规则对象（需含 end_time 或 end）
 * @param {number} [bufferMinutes=CURFEW_END_BUFFER_MINUTES] - 缓冲带宽度（分钟）
 * @returns {number|null} 距宵禁结束的分钟数，不在缓冲带内则返回 null
 */
export const getMinutesFromCurfewEnd = (timeStr, curfewRule, bufferMinutes = CURFEW_END_BUFFER_MINUTES) => {
    if (!timeStr || !curfewRule) return null;

    try {
        const endTime = curfewRule.end_time || curfewRule.end;
        if (!endTime) return null;

        const [hour, minute] = timeStr.split(":").map(Number);
        const [endHour, endMinute] = endTime.split(":").map(Number);
        if ([hour, minute, endHour, endMinute].some((value) => Number.isNaN(value))) {
            return null;
        }

        const currentMinutes = hour * 60 + minute;
        const endMinutes = endHour * 60 + endMinute;

        // 分钟差按 24 小时回绕，保证跨天结束时刻（如 00:00 结束、00:02 检查）也能算出正差值
        const diff = (((currentMinutes - endMinutes) % 1440) + 1440) % 1440;
        return diff < bufferMinutes ? diff : null;
    } catch (error) {
        console.error("计算距宵禁结束时间出错:", error);
        return null;
    }
};

// ========== 航班日期和时间关系处理 ==========

/**
 * 确保航班时间一致性
 * block_time_minutes 不再由此函数维护，事实源为航路主数据（航路选择时写入、重算前刷新时更新）
 * @param {Object} flight - 航班对象，包含std和sta字段
 * @param {string} [timeZone='UTC'] - 时区，默认为UTC
 * @returns {Object} 更新后的航班对象
 */
export const ensureFlightTimeConsistency = (updatedFlight, timeZone = "UTC") => {
    // 深拷贝航班对象，避免修改原对象
    const flight = { ...updatedFlight };

    try {
        // 1. 确保flight_date与STD完全一致，包括时间部分
        if (!flight.std) {
            console.warn("STD缺失，无法确保时间一致性");
            return flight;
        }

        // 使用完整的std值作为flight_date
        flight.flight_date = flight.std;

        // 2. 确保STA不早于STD
        if (flight.sta && flight.std) {
            const stdObj = dayjs.utc(flight.std);
            const staObj = dayjs.utc(flight.sta);

            // 如果STA早于STD，将STA调整为跨天的情况（加一天）
            if (staObj.isBefore(stdObj)) {
                // 获取STA时间部分
                const staHour = staObj.hour();
                const staMinute = staObj.minute();
                const staSecond = staObj.second();

                // 将STA设置为STD日期后一天的同一时间
                flight.sta = stdObj.add(1, "day").hour(staHour).minute(staMinute).second(staSecond).format("YYYY-MM-DDTHH:mm:ss") + "Z";
                console.warn("STA早于STD，已自动调整为跨天", { std: flight.std, oldSta: staObj.format(), newSta: flight.sta });
            }

        }

        // 3. 更新day_of_week字段（如果需要）
        if (flight.flight_date) {
            // day_of_week: 1-7 表示周一至周日，与dayjs中0=周日不同
            const dayOfWeek = dayjs.utc(flight.flight_date).day();
            flight.day_of_week = dayOfWeek === 0 ? 7 : dayOfWeek;
        }

        return flight;
    } catch (error) {
        console.error("确保航班时间一致性时出错:", error);
        return updatedFlight; // 出错时返回原始对象
    }
};

/**
 * 更新航班日期
 * 注意：当修改日期时，需要同时更新STD和STA以保持一致性
 * @param {Object} flight - 航班对象
 * @param {string|Date|dayjs.Dayjs} newDate - 新的日期
 * @param {string} [timeZone='UTC'] - 时区
 * @returns {Object} 更新后的航班对象
 */
export const updateFlightDate = (flight, newDate, timeZone = "UTC") => {
    if (!flight) return flight;

    try {
        // 复制一个新对象，避免修改原对象
        const updatedFlight = { ...flight };

        // 处理newDate为有效的dayjs对象
        let newDateObj;
        try {
            newDateObj = dayjs.utc(newDate);
            if (!newDateObj.isValid()) {
                throw new Error("无效的日期");
            }
        } catch (error) {
            console.error("日期转换错误:", error);
            return updatedFlight;
        }

        // 如果有STD，需要更新STD并同步flight_date
        if (updatedFlight.std) {
            const stdObj = dayjs.utc(updatedFlight.std);

            // 创建新的STD：使用新日期的日期部分和原STD的时间部分
            const newStd = newDateObj.hour(stdObj.hour()).minute(stdObj.minute()).second(stdObj.second()).format("YYYY-MM-DDTHH:mm:ss") + "Z";

            // 更新STD
            updatedFlight.std = newStd;

            // 重要：确保flight_date与std保持完全一致（包括时间部分）
            updatedFlight.flight_date = newStd;
        } else {
            // 如果没有STD，直接设置flight_date为newDate并添加默认时间
            updatedFlight.flight_date = newDateObj.format("YYYY-MM-DDTHH:mm:ss") + "Z";

            // 同步更新std与flight_date保持一致
            updatedFlight.std = updatedFlight.flight_date;
        }

        // 如果有STA，也需要更新STA保持与原来的时间差
        if (updatedFlight.sta && updatedFlight.std) {
            const staObj = dayjs.utc(updatedFlight.sta);
            const oldStdObj = dayjs.utc(flight.std); // 旧的STD
            const newStdObj = dayjs.utc(updatedFlight.std); // 新的STD

            // 计算原始的STD和STA之间的分钟差
            const minutesDiff = staObj.diff(oldStdObj, "minute");

            // 使用相同的分钟差应用到新的STD上得到新的STA
            updatedFlight.sta = newStdObj.add(minutesDiff, "minute").format("YYYY-MM-DDTHH:mm:ss") + "Z";
        }

        // 最后再次确认flight_date与std完全一致，这是核心规则
        updatedFlight.flight_date = updatedFlight.std;

        // 返回处理完成的航班对象
        return updatedFlight;
    } catch (error) {
        console.error("更新航班日期时出错:", error);
        return flight;
    }
};

/**
 * 更新STD时间，保持与flight_date和STA的关系
 * 当STD时间更改时：
 * 1. 应与flight_date同步
 * 2. 如果有飞行时间，重新计算STA
 * 3. 如果没有飞行时间但有STA，保持STA与STD的时间差不变
 *
 * @param {Object} flight - 航班对象
 * @param {dayjs.Dayjs|string|Date} newStdTime - 新的STD时间
 * @returns {Object} 更新后的航班对象
 */
export const updateStdTime = (flight, newStdTime, timeZone = "UTC") => {
    if (!flight || !newStdTime) return flight;

    try {
        // 复制航班对象
        const updatedFlight = { ...flight };

        // 获取航班日期对象 (UTC)
        const flightDateObj = dayjs.utc(updatedFlight.flight_date || dayjs.utc());

        // 处理新的STD时间 - 需要考虑时区
        let stdTimeObj;

        if (timeZone === "UTC") {
            // 如果输入的时间已经是UTC格式，直接使用
            stdTimeObj = dayjs.utc(newStdTime);
        } else {
            // 如果输入的时间是本地时区，需要转换
            stdTimeObj = dayjs(newStdTime);
            // 这里仅提取时间部分，日期部分使用flight_date
        }

        // 创建新的UTC STD时间
        updatedFlight.std = flightDateObj.hour(stdTimeObj.hour()).minute(stdTimeObj.minute()).second(stdTimeObj.second()).format("YYYY-MM-DDTHH:mm:ss") + "Z";

        // 确保flight_date与std完全一致
        updatedFlight.flight_date = updatedFlight.std;

        // 如果有STA和飞行时间，根据新的STD重新计算STA
        if (updatedFlight.min_block_time_minutes && updatedFlight.min_block_time_minutes > 0) {
            const stdDate = dayjs.utc(updatedFlight.std);
            updatedFlight.sta = stdDate.add(updatedFlight.min_block_time_minutes, "minute").format("YYYY-MM-DDTHH:mm:ss") + "Z";
        } else if (updatedFlight.sta) {
            // 保留STA与STD的时间差
            const oldStdDate = dayjs.utc(flight.std);
            const oldStaDate = dayjs.utc(flight.sta);
            const timeDiffMinutes = oldStaDate.diff(oldStdDate, "minute");

            const newStdDate = dayjs.utc(updatedFlight.std);
            updatedFlight.sta = newStdDate.add(timeDiffMinutes, "minute").format("YYYY-MM-DDTHH:mm:ss") + "Z";
        }

        return updatedFlight;
    } catch (error) {
        console.error("更新STD时间出错:", error);
        return flight;
    }
};

/**
 * 更新STA时间，保持与STD的关系，处理跨天情况
 * 当STA时间更改时：
 * 1. 应检查与STD的关系，特别是跨天情况
 * 2. 如有必要，更新飞行时间
 *
 * @param {Object} flight - 航班对象
 * @param {dayjs.Dayjs|string|Date} newStaTime - 新的STA时间
 * @returns {Object} 更新后的航班对象
 */
export const updateStaTime = (flight, newStaTime, timeZone = "UTC") => {
    if (!flight || !newStaTime || !flight.std) return flight;

    try {
        // 复制航班对象
        const updatedFlight = { ...flight };

        // 获取STD日期对象 (UTC)
        const stdDate = dayjs.utc(updatedFlight.std);

        // 处理新的STA时间 - 需要考虑时区
        let staTimeObj;

        if (timeZone === "UTC") {
            // 如果输入的时间已经是UTC格式，直接使用
            staTimeObj = dayjs.utc(newStaTime);
        } else {
            // 如果输入的时间是本地时区，需要转换
            staTimeObj = dayjs(newStaTime);
            // 这里仅提取时间部分，日期部分基于STD处理
        }

        // 计算STD和新STA的时间（转为分钟）
        const stdMinutes = stdDate.hour() * 60 + stdDate.minute();
        const staMinutes = staTimeObj.hour() * 60 + staTimeObj.minute();

        // 判断是否跨天
        if (staMinutes < stdMinutes) {
            // STA在STD之前的时间，说明跨天了
            updatedFlight.sta =
                stdDate
                    .add(1, "day") // 加一天
                    .hour(staTimeObj.hour())
                    .minute(staTimeObj.minute())
                    .second(staTimeObj.second())
                    .format("YYYY-MM-DDTHH:mm:ss") + "Z";
        } else {
            // 同一天
            updatedFlight.sta = stdDate.hour(staTimeObj.hour()).minute(staTimeObj.minute()).second(staTimeObj.second()).format("YYYY-MM-DDTHH:mm:ss") + "Z";
        }

        // 更新飞行时间（如果需要）
        if (updatedFlight.std && updatedFlight.sta) {
            const newStdDate = dayjs.utc(updatedFlight.std);
            const newStaDate = dayjs.utc(updatedFlight.sta);
            updatedFlight.min_block_time_minutes = newStaDate.diff(newStdDate, "minute");
        }

        return updatedFlight;
    } catch (error) {
        console.error("更新STA时间出错:", error);
        return flight;
    }
};

// ========== 导出模块 ==========

/**
 * 判断两个时间是否是同一天（考虑时区）
 * 注意：这是timeUtils自己的实现，不依赖timezoneUtils以避免循环依赖
 *
 * @param {dayjs.Dayjs|string|Date} time1 - 第一个时间
 * @param {dayjs.Dayjs|string|Date} time2 - 第二个时间
 * @param {string} timeZone - 时区
 * @returns {boolean} 是否是同一天
 */
export const isSameDay = (time1, time2, timeZone = "UTC") => {
    try {
        if (!time1 || !time2) return false;

        // 转换为指定时区的dayjs对象
        let date1, date2;

        if (timeZone === "UTC") {
            date1 = dayjs.utc(time1);
            date2 = dayjs.utc(time2);
        } else {
            date1 = dayjs.utc(time1).tz(timeZone);
            date2 = dayjs.utc(time2).tz(timeZone);
        }

        if (!date1.isValid() || !date2.isValid()) return false;

        // 判断是否是同一天
        return date1.format("YYYY-MM-DD") === date2.format("YYYY-MM-DD");
    } catch (error) {
        console.error("判断同一天出错:", error);
        return false;
    }
};

/**
 * 计算两个UTC时间点之间的地面时间（分钟）
 * 适用于航班间的地面停留时间计算，确保正确处理跨天情况
 * @param {string} arrivalTimeUtc - 到达时间 (UTC ISO格式)
 * @param {string} departureTimeUtc - 下一次起飞时间 (UTC ISO格式)
 * @returns {number|null} 地面时间（分钟）
 */
export const calculateGroundTime = (arrivalTimeUtc, departureTimeUtc) => {
    if (!arrivalTimeUtc || !departureTimeUtc) {
        return 0; // Return 0 for consistency with original behavior
    }
    const arrival = dayjs(arrivalTimeUtc);
    const departure = dayjs(departureTimeUtc);

    if (!arrival.isValid() || !departure.isValid()) {
        return 0; // Return 0 for consistency
    }

    const groundTimeMinutes = departure.diff(arrival, "minute");

    // Return 0 if the time is negative, consistent with original logic
    return groundTimeMinutes < 0 ? 0 : groundTimeMinutes;
};

/**
 * 批量计算并设置航班的实际地面时间 (ground_time_minutes)
 * 地面时间 = 当前航班的 STA 到下一航班的 STD 之间的时间差
 * 
 * 适用场景：
 * - 邮件发送时确保 ground_time_minutes 数据完整
 * - 数据导出时补充缺失的地面时间字段
 * 
 * @param {Array} flights - 航班数组，每个航班对象应包含 std, sta, aircraft_reg 字段
 * @returns {Array} 更新后的航班数组，包含计算好的 ground_time_minutes 字段
 */
export const calculateAndSetActualGroundTimes = (flights) => {
    if (!flights || flights.length === 0) {
        return flights;
    }
    if (flights.length === 1) {
        // 只有一个航班时，地面时间为 null（无后续航班）
        return [{ ...flights[0], ground_time_minutes: null }];
    }

    // 1. 按飞机注册号和起飞时间排序
    const sortedFlights = [...flights].sort((a, b) => {
        if (a.aircraft_reg !== b.aircraft_reg) {
            return (a.aircraft_reg || "").localeCompare(b.aircraft_reg || "");
        }
        const stdA = dayjs.utc(a.std);
        const stdB = dayjs.utc(b.std);
        if (!stdA.isValid() && !stdB.isValid()) return 0;
        if (!stdA.isValid()) return 1;
        if (!stdB.isValid()) return -1;
        return stdA.valueOf() - stdB.valueOf();
    });

    // 2. 使用 Map 存储更新后的航班，方便后续恢复原始顺序
    const updatedFlightsMap = new Map();
    // 初始化所有航班的 ground_time_minutes 为 null
    flights.forEach((flight) => {
        updatedFlightsMap.set(flight.id, { ...flight, ground_time_minutes: null });
    });

    // 3. 遍历排序后的航班列表，计算前一航班的地面时间
    for (let i = 1; i < sortedFlights.length; i++) {
        const currentFlight = sortedFlights[i];
        const prevFlight = sortedFlights[i - 1];

        // 检查是否同一架飞机且时间有效
        if (
            prevFlight.aircraft_reg === currentFlight.aircraft_reg &&
            prevFlight.sta && dayjs.utc(prevFlight.sta).isValid() &&
            currentFlight.std && dayjs.utc(currentFlight.std).isValid()
        ) {
            // 计算 prevFlight 降落后到 currentFlight 起飞前的地面时间
            const actualGroundTime = calculateGroundTime(prevFlight.sta, currentFlight.std);

            // 更新 Map 中对应的 prevFlight
            const updatedPrevFlight = updatedFlightsMap.get(prevFlight.id);
            if (updatedPrevFlight) {
                updatedPrevFlight.ground_time_minutes = actualGroundTime;
            }
        }
    }

    // 4. 按原始顺序返回更新后的航班列表
    return flights.map((originalFlight) => updatedFlightsMap.get(originalFlight.id));
};

/**
 * Formats a duration in minutes into a specified string format.
 * @param {number} totalMinutes - Duration in minutes.
 * @param {'HH:mm' | 'h' | 'h_m'} format - The output format.
 * @returns {string} - The formatted duration string.
 */
export const formatDuration = (totalMinutes, format = "HH:mm") => {
    if (typeof totalMinutes !== "number" || isNaN(totalMinutes) || totalMinutes < 0) return "-";

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (format === "HH:mm") {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }

    if (format === "h") {
        const roundedHours = Math.round(totalMinutes / 60);
        return `${roundedHours}h`;
    }

    if (format === "h_m") {
        if (totalMinutes === 0) return "0m";
        const hourPart = hours > 0 ? `${hours}h` : "";
        const minutePart = minutes > 0 ? `${minutes}m` : "";
        return [hourPart, minutePart].filter(Boolean).join("");
    }

    return "-";
};

/**
 * Calculates block hours between two UTC date strings.
 * @param {string} std - Start time string (ISO format).
 * @param {string} sta - End time string (ISO format).
 * @returns {number|null} - The block time in minutes, or null if invalid.
 */
export const calculateBlockHours = (std, sta) => {
    if (!std || !sta) return null;
    try {
        const startTime = dayjs(std);
        const endTime = dayjs(sta);
        if (!startTime.isValid() || !endTime.isValid()) return null;

        return endTime.diff(startTime, "minute");
    } catch (e) {
        return null;
    }
};

// ========== 航班时间策略 ==========

/**
 * 航班时间策略 - 获取航班的权威时间
 * 学习后端的时间选择策略，按优先级独立选择起飞和到达时间
 *
 * @param {Object} flight - 航班对象
 * @param {string} timelineType - 时间轴类型 ("plan" | "actual")
 * @returns {Object} 包含权威时间和策略信息的对象
 */
export const getFlightAuthoritativeTimes = (flight, timelineType = "actual") => {
    if (timelineType === "plan" || timelineType === "editor") {
        // 监控计划轴与编辑器同一口径：有预计用预计，没有再用计划；不吃实际时间。
        const editorDepartureFields = [
            { field: "etd", strategy: "estimated" },
            { field: "std", strategy: "scheduled" },
        ];
        const editorArrivalFields = [
            { field: "eta", strategy: "estimated" },
            { field: "sta", strategy: "scheduled" },
        ];

        let depTime = null;
        let depStrategy = "none";
        let depField = null;
        for (const { field, strategy } of editorDepartureFields) {
            if (flight[field]) {
                depTime = flight[field];
                depStrategy = strategy;
                depField = field;
                break;
            }
        }

        let arrTime = null;
        let arrStrategy = "none";
        let arrField = null;
        for (const { field, strategy } of editorArrivalFields) {
            if (flight[field]) {
                arrTime = flight[field];
                arrStrategy = strategy;
                arrField = field;
                break;
            }
        }

        return {
            departureTime: depTime,
            arrivalTime: arrTime,
            departureStrategy: depStrategy,
            arrivalStrategy: arrStrategy,
            departureField: depField,
            arrivalField: arrField,
        };
    }

    // 实际时间轴：按照降级优先级选择时间
    // 起飞时间优先级: settlement_atd -> atd -> atd_icao -> etd -> std
    const departureFields = [
        { field: "settlement_atd", strategy: "settlement" },
        { field: "atd", strategy: "actual" },
        { field: "atd_icao", strategy: "icao" },
        { field: "etd", strategy: "estimated" },
        { field: "std", strategy: "scheduled" },
    ];

    // 到达时间优先级: settlement_ata -> ata -> ata_icao -> eta -> sta
    const arrivalFields = [
        { field: "settlement_ata", strategy: "settlement" },
        { field: "ata", strategy: "actual" },
        { field: "ata_icao", strategy: "icao" },
        { field: "eta", strategy: "estimated" },
        { field: "sta", strategy: "scheduled" },
    ];

    let departureTime = null;
    let departureStrategy = "none";
    let departureField = null;
    for (const { field, strategy } of departureFields) {
        const timeVal = flight[field];
        if (timeVal) {
            departureTime = timeVal;
            departureStrategy = strategy;
            departureField = field;
            break;
        }
    }

    let arrivalTime = null;
    let arrivalStrategy = "none";
    let arrivalField = null;
    for (const { field, strategy } of arrivalFields) {
        const timeVal = flight[field];
        if (timeVal) {
            arrivalTime = timeVal;
            arrivalStrategy = strategy;
            arrivalField = field;
            break;
        }
    }

    return {
        departureTime,
        arrivalTime,
        departureStrategy,
        arrivalStrategy,
        departureField,
        arrivalField,
    };
};

/**
 * 获取航班时间策略的简化版本 - 仅返回时间值
 * @param {Object} flight - 航班对象
 * @param {string} timelineType - 时间轴类型 ("plan" | "actual")
 * @returns {Object} 包含起飞和到达时间的对象
 */
export const getFlightTimes = (flight, timelineType = "actual") => {
    const result = getFlightAuthoritativeTimes(flight, timelineType);
    return {
        departureTime: result.departureTime,
        arrivalTime: result.arrivalTime,
    };
};

/**
 * 获取航班时间策略的详细信息 - 用于调试和日志
 * @param {Object} flight - 航班对象
 * @param {string} timelineType - 时间轴类型 ("plan" | "actual")
 * @returns {Object} 包含所有时间字段和策略信息的详细对象
 */
export const getFlightTimeDetails = (flight, timelineType = "actual") => {
    const authTimes = getFlightAuthoritativeTimes(flight, timelineType);

    return {
        ...authTimes,
        // 包含所有原始时间字段，用于调试
        allTimes: {
            std: flight.std,
            etd: flight.etd,
            atd: flight.atd,
            atd_icao: flight.atd_icao,
            sta: flight.sta,
            eta: flight.eta,
            ata: flight.ata,
            ata_icao: flight.ata_icao,
        },
        // 策略描述
        strategyDescription: `${authTimes.departureStrategy} → ${authTimes.arrivalStrategy}`,
        // 字段描述
        fieldDescription: `${authTimes.departureField} → ${authTimes.arrivalField}`,
    };
};

/**
 * 取"编辑器口径"的起飞时间：ETD 优先，没有就 STD。ATD 不参与。
 * 用于航班计划编辑器的导入和对比，让编辑器跟监控页保持口径一致，
 * 又不把已发生的延误带进未来的草案。
 */
export const pickEditorDepartureTime = (flight) => {
    if (!flight) return null;
    return flight.etd ?? flight.std ?? null;
};

/**
 * 取"编辑器口径"的到达时间：ETA 优先，没有就 STA。ATA 不参与。
 * 与 pickEditorDepartureTime 完全独立——起飞和到达各自判断。
 */
export const pickEditorArrivalTime = (flight) => {
    if (!flight) return null;
    return flight.eta ?? flight.sta ?? null;
};

export default {
    DATE_FORMATS,
    isValidDate,
    // 基础时间处理
    formatDate,
    createIsoTimeString,
    extractTimeFromDateTime,
    timeToMinutes,

    // 航班时间处理
    calculateNextFlightTime,
    generateFlightNumber,
    calculateGroundTime,
    calculateAndSetActualGroundTimes,

    // 约束时间处理
    hasMinimumTimeDifference,
    formatTimeRangeRule,

    // 类型处理与安全操作
    ensureDayjsObject,
    safelyCallDayjsMethod,
    safelyAddTime,
    safelyFormatDate,
    createDateTime,
    getMinutesToCurfew,
    CURFEW_END_BUFFER_MINUTES,
    getMinutesFromCurfewEnd,

    // 航班日期和时间关系处理
    ensureFlightTimeConsistency,
    updateFlightDate,
    updateStdTime,
    updateStaTime,

    // 时区处理
    convertToUTC,
    convertFromUTC,
    formatTimeWithZone,
    isSameDay,

    // New functions
    formatDuration,
    calculateBlockHours,

    // 航班时间策略
    getFlightAuthoritativeTimes,
    getFlightTimes,
    getFlightTimeDetails,
    pickEditorDepartureTime,
    pickEditorArrivalTime,
};
